/**
 * Exporta tabelas para um .xlsx de verdade (uma aba por tabela).
 *
 * Números vão como número e datas como data — não como texto —, para a
 * planilha já sair pronta para somar, filtrar e fazer gráfico. O exceljs
 * é carregado só no clique (import dinâmico): é pesado e quase ninguém
 * exporta, então não entra no carregamento do sistema.
 *
 *   await exportarExcel("cotacoes_2026-10-09", [
 *     { nome: "Soja", titulo: "Soja Spot - R$/sc (MT)", colunas: [
 *         { titulo: "Data", chave: "data", tipo: "data" },
 *         { titulo: "Valor", chave: "valor", tipo: "numero" },
 *       ], linhas: [{ data: "01/10/2026", valor: 150 }] },
 *   ]);
 */

/** "1.234,56" | "1234.56" | 150 -> número; vazio, "-" ou inválido -> null. */
export function paraNumero(v) {
  if (v === null || v === undefined || v === "" || v === "-") return null;
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  const t = String(v).trim();
  const n = Number(/,\d+$/.test(t) ? t.replace(/\./g, "").replace(",", ".") : t);
  return Number.isFinite(n) ? n : null;
}

/** "DD/MM/AAAA" ou "AAAA-MM-DD..." -> Date em UTC (o Excel não tem fuso:
 *  em UTC o dia não muda na conversão). */
export function paraData(v) {
  if (!v) return null;
  if (v instanceof Date) return new Date(Date.UTC(v.getFullYear(), v.getMonth(), v.getDate()));
  let m = String(v).match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (m) return new Date(Date.UTC(+m[3], +m[2] - 1, +m[1]));
  m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
  return null;
}

// Nome de aba: até 31 caracteres, sem : \ / ? * [ ], e sem repetir.
function nomeDeAba(nome, usados) {
  const base = String(nome || "Planilha").replace(/[:\\/?*[\]]/g, "-").trim().slice(0, 31) || "Planilha";
  let n = base;
  for (let i = 2; usados.has(n.toLowerCase()); i++) n = `${base.slice(0, 31 - String(i).length - 1)} ${i}`;
  usados.add(n.toLowerCase());
  return n;
}

export async function exportarExcel(nomeArquivo, abas) {
  const { default: ExcelJS } = await import("exceljs");
  const wb = new ExcelJS.Workbook();
  wb.creator = "Rural Business";
  wb.created = new Date();
  const usados = new Set();

  for (const aba of abas) {
    const ws = wb.addWorksheet(nomeDeAba(aba.nome, usados));
    let linhaCab = 1;
    if (aba.titulo) {
      ws.getCell(1, 1).value = aba.titulo;
      ws.getCell(1, 1).font = { bold: true, size: 12 };
      linhaCab = 3;
    }

    const cab = ws.getRow(linhaCab);
    aba.colunas.forEach((c, i) => {
      const cel = cab.getCell(i + 1);
      cel.value = c.titulo;
      cel.font = { bold: true, color: { argb: "FFFFFFFF" } };
      cel.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1F3864" } };
      cel.alignment = { horizontal: "center" };
    });

    aba.linhas.forEach((linha, li) => {
      const row = ws.getRow(linhaCab + 1 + li);
      aba.colunas.forEach((c, ci) => {
        const cel = row.getCell(ci + 1);
        const bruto = linha[c.chave];
        if (c.tipo === "numero") {
          cel.value = paraNumero(bruto);
          cel.numFmt = c.formato || "#,##0.00";
        } else if (c.tipo === "data") {
          cel.value = paraData(bruto) ?? (bruto ?? null);
          cel.numFmt = "dd/mm/yyyy";
        } else {
          cel.value = bruto ?? null;
        }
      });
    });

    // Largura pelo maior conteúdo da coluna (texto ou número formatado).
    aba.colunas.forEach((c, ci) => {
      const maior = Math.max(
        String(c.titulo).length,
        ...aba.linhas.map((l) => String(l[c.chave] ?? "").length + (c.tipo === "numero" ? 3 : 0))
      );
      ws.getColumn(ci + 1).width = Math.min(Math.max(maior + 2, 10), 50);
    });
    ws.views = [{ state: "frozen", ySplit: linhaCab }];
    ws.autoFilter = { from: { row: linhaCab, column: 1 }, to: { row: linhaCab, column: aba.colunas.length } };
  }

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${nomeArquivo}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
